import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalProps } from "../../../Shared/types/ModalProps";
import { GenericButton } from "../../../Shared/components/GenericButton";
import { GenericInput } from "../../../Shared/components/GenericInput";
import { GenericSelectInput } from "../../../Shared/components/GenericSelectInput";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { IUser } from "../models/User";
import { CreateUserInput } from "../models/CreateUserInput";
import { UserService } from "../services/user.service";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { roleTypeOptions } from "../data/role";
import { AuthService } from "../../Auth/services/AuthService";

type UserFormProps = ModalProps & {
  user?: IUser;
};

export const UserForm: React.FC<UserFormProps> = ({ onClose, user }) => {
  const { t } = useTranslation();
  const message = t('loading_message');
  const role = AuthService.getLocalUser()?.user?.roles?.[0];
  const roleOptions = roleTypeOptions(role?.name);

  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const [roleType, setRoleType] = useState(user?.roles?.[0]?.id || "");
  const [roleTypeError, setRoleTypeError] = useState("");

  const [country, setCountry] = useState("");
  const [countryError, setCountryError] = useState(user?.country || "");
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [firstNameError, setLastNameError] = useState("");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [lastNameError, setFirstNameError] = useState("");
  const [middleName, setMiddleName] = useState(user?.middleName || "");
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || "");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPPassword] = useState("");
  const [confirmPasswordError, setConfirmPPasswordError] = useState("");

  const onCountry = (val: string) => {
    setCountry(val);
    setCountryError(val ? "" : "");
  };

  const onRoleType = (val: string) => {
    setRoleType(val);
    setRoleTypeError(val ? "" : "");
  };

  const onFirstName = (val: string) => {
    setFirstName(val);
    setFirstNameError(val.trim() === "" ? "" : "");
  };
  const onMiddleName = (val: string) => {
    setMiddleName(val);
  };
  const onLastName = (val: string) => {
    setLastName(val);
    setLastNameError(val.trim() === "" ? "" : "");
  };
  const onPhoneNumber = (val: string) => {
    setPhoneNumber(val);
    setPhoneNumberError(val.trim() === "" ? "" : "");
  };

  const onEmail = (val: string) => {
    setEmail(val);
    setEmailError(val.includes("@") ? "" : "");
  };

  const onPassword = (val: string) => {
    setPassword(val);
    setPasswordError(val?.length < 4 ? "" : "");
  };

  const onConfirmPassword = (val: string) => {
    setConfirmPPassword(val);
    setConfirmPPasswordError(val.trim() === "" ? " " : "");
  };

  const resetForm = () => {
    setCountry("");
    setCountryError("");

    setRoleType("");
    setRoleTypeError("");

    setFirstName("");
    setFirstNameError("");

    setLastName("");
    setLastNameError("");

    setMiddleName("");

    setPhoneNumber("");
    setPhoneNumberError("");

    setEmail("");
    setEmailError("");

    setPassword("");
    setPasswordError("");

    setConfirmPPassword("");
    setConfirmPPasswordError("");
  };

  const isFirstNameValid = firstName.trim() !== "";
  const isLastNameValid = lastName.trim() !== "";
  const isPhoneNumberValid = phoneNumber.trim() !== "";
  const isEmailValid = email.trim() !== "" && email.includes("@");
  const isPasswordValid = password.trim() !== "" && password.length >= 4;
  const isConfirmPasswordValid =
    confirmPassword.trim() !== "" && confirmPassword === password;

  const isFormValid =
    isFirstNameValid &&
    isLastNameValid &&
    isPhoneNumberValid &&
    isEmailValid &&
    isPasswordValid &&
    isConfirmPasswordValid &&
    !firstNameError &&
    !lastNameError &&
    !phoneNumberError &&
    !emailError &&
    !passwordError &&
    !confirmPasswordError;

  const createAccountAction = async (e: React.FormEvent) => {
    setIsLoading(true);
    e.preventDefault();
    const registerInput = new CreateUserInput();
    if (user?.id) {
      registerInput.id = user.id;
    }
    registerInput.country = country.trim();
    registerInput.firstName = firstName.trim();
    registerInput.middleName = middleName.trim();
    registerInput.lastName = lastName.trim();
    registerInput.phoneNumber = phoneNumber.trim();
    registerInput.email = email.trim();
    registerInput.password = password.trim();
    registerInput.role = roleType ?? "3";

    try {
      await UserService.create(registerInput);
      resetForm();
      onClose(true);
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

const countryOptions = t('country_options', { returnObjects: true }) as { label: string, value: string }[];
  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg w-full max-w-md p-5 mt-10 mb-10 max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center pb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {user?.id ? "Update User" : "Create User"}
            </h3>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="relative">
            <div className="w-full h-100">
              <form className="mt-3 mb-3" onSubmit={createAccountAction}>
                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericSelectInput
                    label="Country"
                    containerClassName=""
                    options={countryOptions}
                    value={country}
                    onChange={onCountry}
                    required
                    placeholder="Select a country"
                    error={countryError}
                  />
                  <GenericInput
                    containerClassName=""
                    label=" First Name"
                    value={firstName}
                    onChange={onFirstName}
                    type="text"
                    error={firstNameError}
                    placeholder="Enter your first name"
                    required={true}
                  />
                </div>

                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <GenericInput
                    containerClassName=""
                    label=" Middle Name"
                    value={middleName}
                    onChange={onMiddleName}
                    type="text"
                    placeholder="Enter your middle name"
                    required={false}
                  />
                  <GenericInput
                    containerClassName=""
                    label=" Last Name"
                    value={lastName}
                    onChange={onLastName}
                    type="text"
                    error={lastNameError}
                    placeholder="Enter your last name"
                    required={true}
                  />
                </div>

                <GenericInput
                  label=" Phone Number"
                  value={phoneNumber}
                  onChange={onPhoneNumber}
                  type="text"
                  error={phoneNumberError}
                  placeholder="Enter your phone number"
                  required={true}
                />

                <GenericInput
                  label="Email"
                  value={email}
                  onChange={onEmail}
                  type="email"
                  error={emailError}
                  placeholder="Enter your email"
                  required={true}
                />

                <GenericSelectInput
                  label="Role"
                  options={roleOptions}
                  value={roleType}
                  onChange={onRoleType}
                  required
                  placeholder="Select a role"
                  error={roleTypeError}
                />

                <div className="grid gap-6 mb-6 md:grid-cols-2 mb-6">
                  <GenericInput
                    containerClassName=""
                    label="Password"
                    value={password}
                    onChange={onPassword}
                    type="password"
                    error={passwordError}
                    placeholder="Enter your password"
                    required={true}
                  />

                  <GenericInput
                    containerClassName=""
                    label="Confirm Password"
                    value={confirmPassword}
                    onChange={onConfirmPassword}
                    type="password"
                    error={confirmPasswordError}
                    placeholder="Confirm your password"
                    required={true}
                  />
                </div>
                <div className="mx-5">
                  <GenericButton type="submit" disabled={!isFormValid}>
                    {user?.id ? "Update User" : "Create User"}
                  </GenericButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
